/**
 * Cliente GraphQL contra Suwayomi-Server.
 *
 * En desarrollo el proxy de Vite inyecta la autenticacion basica, asi que
 * aqui no hace falta cabecera. En produccion la PWA se sirve desde el mismo
 * dominio y las credenciales se guardan en el dispositivo tras el login.
 */

const RUTA_API = '/api/graphql'
const CLAVE_CREDENCIALES = 'yomou.credenciales'

export interface Credenciales {
  usuario: string
  clave: string
}

export function leerCredenciales(): Credenciales | null {
  const crudo = localStorage.getItem(CLAVE_CREDENCIALES)
  if (!crudo) return null
  try {
    return JSON.parse(crudo) as Credenciales
  } catch {
    // Guardado corrupto: mejor olvidarlo que arrastrar el fallo en cada peticion
    localStorage.removeItem(CLAVE_CREDENCIALES)
    return null
  }
}

export function guardarCredenciales(credenciales: Credenciales | null) {
  if (credenciales) {
    localStorage.setItem(CLAVE_CREDENCIALES, JSON.stringify(credenciales))
  } else {
    localStorage.removeItem(CLAVE_CREDENCIALES)
  }
}

export function cabecerasAuth(): Record<string, string> {
  const credenciales = leerCredenciales()
  if (!credenciales) return {}
  return {
    Authorization: 'Basic ' + btoa(`${credenciales.usuario}:${credenciales.clave}`),
  }
}

/** Error con la informacion util de GraphQL ya extraida. */
export class ErrorApi extends Error {
  constructor(
    mensaje: string,
    readonly estado?: number,
    readonly esAutenticacion = false,
  ) {
    super(mensaje)
    this.name = 'ErrorApi'
  }
}

export async function gql<T>(consulta: string, variables?: Record<string, unknown>): Promise<T> {
  let respuesta: Response
  try {
    respuesta = await fetch(RUTA_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...cabecerasAuth() },
      body: JSON.stringify({ query: consulta, variables }),
    })
  } catch {
    throw new ErrorApi('No se pudo conectar con el servidor')
  }

  if (respuesta.status === 401) {
    throw new ErrorApi('Usuario o contrasena incorrectos', 401, true)
  }
  if (!respuesta.ok) {
    throw new ErrorApi(`El servidor respondio ${respuesta.status}`, respuesta.status)
  }

  const cuerpo = (await respuesta.json()) as { data?: T; errors?: { message: string }[] }

  if (cuerpo.errors?.length) {
    // Suwayomi devuelve el stacktrace de Java entero en el mensaje. Solo la
    // primera linea es legible; el resto es ruido para el usuario.
    const bruto = cuerpo.errors[0]!.message
    const limpio = bruto.split('\n')[0]!.replace(/^Exception while fetching data \(\/\w+\) : /, '')
    throw new ErrorApi(limpio)
  }

  if (cuerpo.data === undefined) {
    throw new ErrorApi('El servidor no devolvio datos')
  }
  return cuerpo.data
}

/**
 * Construye la URL de una imagen servida por Suwayomi (portadas y paginas).
 *
 * Se devuelve la ruta relativa a proposito: al ser same-origin en produccion
 * y pasar por el proxy en desarrollo, funciona en ambos casos. Las imagenes
 * no se ponen directamente en <img src> cuando hay autenticacion, sino que
 * se descargan con fetch para poder cachearlas offline.
 */
export function urlImagen(ruta: string): string {
  return ruta.startsWith('/') ? ruta : `/${ruta}`
}
